// **الجلسة تُقفل إن هُجرت الشاشة** (قرار المالك 22/09/2026: «عاوزه لو مستخدمش الماوس
// على الشاشة بتاعته لمدة 10 دقايق يفصل الـsession ويطلب منه باصورد تاني»).
//
// والسبب ظاهرٌ في مكتبٍ فيه أجهزةٌ مشتركة وشاشاتٌ تُترك مفتوحة: من قام ونسي شاشته
// مفتوحةً تركها لمن يمرّ — بحجوزاته وأرقام عملائه ومرتّبه. والقفل التلقائي هو ما يغلق
// هذا الباب بلا أن يُعتمد على أن يتذكّر أحد.
//
// **وتُقاس المهلة بالساعة لا بعدّاد** (`Date.now()` لا `setTimeout` وحده): الجهاز ينام
// والتبويب يُجمَّد، والعدّادُ فيهما يتوقّف أو يتباطأ — فيبقى المهجورُ مفتوحًا ساعاتٍ
// وهو «لم تمضِ عليه عشر دقائق». فيُسجَّل وقتُ آخر حركة، ويُقارَن عند كل استيقاظ.
//
// **وكل حركةٍ تُعيد العدّ**: الماوس، ولوحة المفاتيح، واللمس، والتمرير. ولا يُعاد ضبط
// المؤقّت مع كل بكسل — يُسجَّل الوقت فقط، ويصحّح المؤقّتُ نفسه عند انتهائه.
import { useEffect, useRef } from 'react';
import { LAST_ACTIVE_KEY } from '../lib/api.ts';

/** ما يُعدّ حياةً على الشاشة. و`passive` لأنها لا تمنع شيئًا — فلا تُبطئ التمرير. */
const SIGNS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;

/**
 * يُنهي الجلسة بعد `minutes` من السكون.
 *
 * @param enabled يُشغَّل بعد الدخول وحده — ولا معنى له على شاشة الدخول.
 * @param onIdle  يُنادى مرّةً واحدة عند انقضاء المهلة.
 */
/**
 * **المدّة في مكانٍ واحد** — تُقرأ من الشاشة التي تُقفل ومن الرسالة التي تشرح لماذا.
 *
 * وكانت عشرةً في `App.tsx` وعشرةً مكتوبةً بالحرف في جملة شاشة الدخول. فمن غيّرها
 * يومًا في موضعٍ نسي الآخر، فتُقفل الجلسة بعد خمسٍ وتقول للمستخدم «عشرة» — وهو أسوأ
 * من ألّا تقول شيئًا: رقمٌ يُقرأ على أنه حقيقة وليس كذلك.
 */
/**
 * **ساعة لا عشر دقائق** (قرار المالك 01/10/2026: «عاوز البرنامج يقفل لو اتساب ساعة من غير ما حد يروح عليه بدل 10 دقايق،
 * ويفضل مفتوح على الموظف — يفضل أونلاين ويوصله الإشعارات والتنبيهات»): العشر كانت تقفل على الموظف وهو في مكالمةٍ أو
 * عند عميل، فيختفي من «الأونلاين» ولا تصله رسالة ولا طلب موقع حتى يرجع ويسجّل دخوله. وما دامت الجلسة مفتوحة فالاستطلاع
 * يعمل وحده (الإشعارات والشات وطلبات الموقع) ويُبقي `last_seen_at` حيًّا — فلا شيء آخر يلزم غير المهلة.
 */
export const IDLE_MINUTES = 60;

/** المدّة بلسان الناس: «ساعة» و«ساعتين» لا «60 دقايق». */
export function idleDurationAr(minutes: number = IDLE_MINUTES): string {
  if (minutes === 60) return 'ساعة';
  if (minutes === 120) return 'ساعتين';
  if (minutes % 60 === 0) return `${minutes / 60} ساعات`;
  return `${minutes} دقايق`;
}

/**
 * `onIdle` يقرّر: `true` (أو لا شيء) ⇒ انتهت الجلسة ولا عدّ بعدها، و`false` ⇒ «لا تقفل الآن» فيبدأ العدّ من جديد —
 * وبه يبقى الحاضر في ورديته داخل البرنامج (قرار المالك 02/10/2026: «متخلّيش البرنامج يخرج خالص إلا لو سجّل انصراف»).
 */
export function useIdleLogout(enabled: boolean, minutes: number, onIdle: () => void | boolean | Promise<boolean | void>): void {
  const fire = useRef(onIdle);
  fire.current = onIdle;

  useEffect(() => {
    if (!enabled || minutes <= 0) return;
    const ms = minutes * 60_000;
    let last = Date.now();
    let timer = 0;
    let done = false;

    /**
     * **السكونُ سكونُ المستخدم لا سكونُ التبويب** (مراجعة 23/09/2026): كان كل تبويبٍ يعدّ وحده،
     * فالتبويبُ المتروك في الخلفية يُنهي الجلسة المشتركة بعد عشر دقائق والمستخدمُ يعمل في غيره.
     * فآخرُ حركةٍ تُكتب في التخزين المشترك (مرّةً كل بضع ثوانٍ لا مع كل بكسل)، والمهلةُ تُقاس من
     * أحدث حركةٍ في أيّ تبويب.
     */
    // ومنه أيضًا يقول المتصفّح للخادم «آخر نشاط» (0155) — الدور في طلبات الموقع لمن تحرّك خلال 30 دقيقة
    const SHARED = LAST_ACTIVE_KEY;
    const sharedLast = (): number => { try { return Number(localStorage.getItem(SHARED)) || 0; } catch { return 0; } };
    let wrote = 0;
    const check = (): void => {
      if (done) return;
      const left = Math.max(last, sharedLast()) + ms - Date.now();
      if (left <= 0) {
        done = true;
        void Promise.resolve(fire.current()).then(ended => {
          if (ended !== false) return;
          // رُفض القفل (حاضرٌ في ورديته): العدّ من جديد بلا اعتبار الصمت حركةً من المستخدم
          done = false; last = Date.now();
          timer = window.setTimeout(check, ms);
        });
        return;
      }
      timer = window.setTimeout(check, left);
    };
    const alive = (): void => {
      last = Date.now();
      if (last - wrote > 5_000) { wrote = last; try { localStorage.setItem(SHARED, String(last)); } catch { /* تخزينٌ ممنوع */ } }
    };
    alive();
    // العودة إلى التبويب: تُقاس المهلة فورًا ولا يُنتظر عدّادٌ كان مجمَّدًا
    const wake = (): void => { if (document.visibilityState === 'visible') check(); };

    for (const s of SIGNS) window.addEventListener(s, alive, { passive: true });
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('focus', wake);
    timer = window.setTimeout(check, ms);

    return () => {
      done = true;
      window.clearTimeout(timer);
      for (const s of SIGNS) window.removeEventListener(s, alive);
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('focus', wake);
    };
  }, [enabled, minutes]);
}
